import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { UbigeoProvider } from '../../../../observatory-single/providers/ubigeo-provider';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle-component';
import { CommunitySearchComponent } from '../../../../observatory-single/components/community-search/community-search-component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, ThemeToggleComponent, CommunitySearchComponent],
  template: `
    <header class="main-header">

      <!-- ── Brand ──────────────────────────────────── -->
      <div class="brand-section">
        <div class="logo-wrap">
          <img src="/assets/midagri_logo.png" alt="MIDAGRI" class="logo-img" />
        </div>
        <div class="brand-divider"></div>
        <div class="geowirpa-logo">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2L30 10V22L16 30L2 22V10L16 2Z" stroke="url(#logo-grad)" stroke-width="2.5" stroke-linejoin="round"/>
            <path d="M16 8C16 8 22 14 22 18C22 21.3137 19.3137 24 16 24C12.6863 24 10 21.3137 10 18C10 14 16 8 16 8Z" fill="url(#logo-grad)" fill-opacity="0.85"/>
            <path d="M6 14L16 19L26 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/>
            <defs>
              <linearGradient id="logo-grad" x1="2" y1="2" x2="30" y2="30" gradientUnits="userSpaceOnUse">
                <stop stop-color="var(--op-green-hi)"/>
                <stop offset="1" stop-color="var(--op-green-mid)"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div class="brand-text">
          <span class="brand-title">Geo<span class="brand-title-alt">View</span></span>
          <span class="brand-subtitle">Dirección General de Gestión Territorial</span>
        </div>
      </div>

      <!-- ── Region selectors ───────────────────────── -->
      <div class="region-selectors">

        <div class="selector-group">
          <p-select
            [options]="regionScope.state().departments"
            optionLabel="label"
            optionValue="value"
            [disabled]=true
            [ngModel]="regionScope.state().selectedDepartment"
            (ngModelChange)="onDeptChange($event)"
            placeholder="Departamento"
            styleClass="region-select" />
        </div>

        <div class="selector-group">
          <p-select
            [options]="regionScope.state().provinces"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            [ngModel]="regionScope.state().selectedProvince"
            (ngModelChange)="onProvChange($event)"
            placeholder="Provincia"
            styleClass="region-select" />
        </div>

        <div class="selector-group">
          <p-select
            [options]="regionScope.state().districts"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            [ngModel]="regionScope.state().selectedDistrict"
            (ngModelChange)="onDistChange($event)"
            placeholder="Distrito"
            styleClass="region-select" />
        </div>

        <!-- Community search -->
        <div class="header-divider"></div>
        <app-community-search></app-community-search>

        <!-- Theme Toggle -->
        <app-theme-toggle></app-theme-toggle>
      </div>
    </header>
  `,
  styles: [`
    :host {
      display: block;
      flex-shrink: 0;
    }

    .main-header {
      --hdr-text: #fff;
      --hdr-text-sub: rgba(255,255,255,0.7);
      --hdr-divider: rgba(255,255,255,0.2);

      display: flex;
      align-items: center;
      gap: 1.5dvw;
      height: auto;
      min-height: clamp(3dvh, 5dvh, 7dvh);
      padding: 0.8dvh 1.5dvw;
      background: var(--op-green-hi);
      border-bottom: 1px solid var(--hdr-divider);
      font-family: var(--op-sans);
      box-sizing: border-box;
      user-select: none;
      transition: background 0.3s ease, border-color 0.3s ease;
    }
    :host-context(body.dark-mode) .main-header {
      --hdr-text: var(--op-green-hi);
      --hdr-text-sub: var(--op-text-dim);
      --hdr-divider: var(--op-border);
      background: var(--op-bg-deep);
    }

    /* ── Brand ───────────────────────────────────────── */
    .brand-section {
      display: flex;
      align-items: center;
      gap: 0.8dvw;
      margin-right: auto;
    }

    .logo-wrap {
      height: clamp(2.8dvh, 4dvh, 5dvh);
      border-radius: 0.8dvh;
      overflow: hidden;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform var(--transition-base);
    }
    .logo-wrap:hover {
      transform: scale(1.05);
    }

    .logo-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .brand-divider {
      width: 1px;
      height: clamp(2dvh, 3dvh, 4dvh);
      background: var(--hdr-divider);
    }

    .geowirpa-logo {
      width: clamp(2.8dvh, 4dvh, 5dvh);
      height: clamp(2.8dvh, 4dvh, 5dvh);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--hdr-text);
      transition: transform var(--transition-spring);
    }
    .geowirpa-logo:hover {
      transform: rotate(15deg) scale(1.1);
    }
    .geowirpa-logo svg {
      width: 85%;
      height: 85%;
    }

    .brand-text {
      display: flex;
      flex-direction: column;
      line-height: 1.1;
    }

    .brand-title {
      font-weight: 800;
      font-size: max(1rem, 1.8dvh);
      color: var(--hdr-text);
      letter-spacing: 0.8px;
      transition: transform var(--transition-base);
    }
    .brand-title-alt {
      font-weight: 500;
    }

    .brand-subtitle {
      font-size: max(0.62rem, 1.1dvh);
      color: var(--hdr-text-sub);
      font-weight: 600;
      letter-spacing: 0.2px;
      transition: color var(--transition-base);
      white-space: nowrap;
    }

    /* ── Region Selectors ────────────────────────────── */
    .region-selectors {
      display: flex;
      align-items: center;
      gap: 0.5dvw;
      margin-left: auto;
    }

    :host ::ng-deep .region-select {
      font-family: var(--op-sans);
      font-size: clamp(0.75rem, 1.5dvh, 0.9rem);
      min-width: 14dvw;
    }

    :host ::ng-deep .region-select .p-select-label {
      font-weight: 600;
      color: var(--op-text-hi);
    }

    :host ::ng-deep .region-select .p-placeholder {
      font-weight: 500;
      color: var(--op-text-dim);
    }

    .header-divider {
      width: 1px;
      height: clamp(1.6dvh, 2.4dvh, 3.2dvh);
      background: var(--hdr-divider);
      flex-shrink: 0;
    }

    /* ── Actions ─────────────────────────────────────── */
    .actions-section {
      display: flex;
      align-items: center;
      gap: 1.5dvw;
      margin-left: auto;
    }

    @keyframes pulse {
      0%   { transform: scale(0.95); box-shadow: 0 0 0 0 color-mix(in srgb, var(--op-green-hi), transparent 30%); }
      70%  { transform: scale(1);    box-shadow: 0 0 0 6px color-mix(in srgb, var(--op-green-hi), transparent 100%); }
      100% { transform: scale(0.95); box-shadow: 0 0 0 0 color-mix(in srgb, var(--op-green-hi), transparent 100%); }
    }
  `],
})
export class HeaderComponent {
  protected readonly regionScope = inject(UbigeoProvider);

  onDeptChange(value: string): void {
    if (value) {
      this.regionScope.notifier.selectDepartment(value);
    }
  }

  onProvChange(value: string): void {
    this.regionScope.notifier.selectProvince(value ?? '');
  }

  onDistChange(value: string): void {
    this.regionScope.notifier.selectDistrict(value ?? '');
  }
}
